import {
  isFunction,
  get,
  map,
  some,
  split,
  trim,
  isArray,
  filter,
  size,
  values,
  find,
} from 'lodash';
import sortUtils from './sort-utils';

function getMaskedText(item, itemLabel, search) {
  /* This method is needed to mask the items when querying. It will split the text the user sees into three
      parts: start, middle and end. The middle is the part that gets highlighted on the row item.
      For more, check the test file for the final output.
    */
  if (!itemLabel) return;
  const rowText = isFunction(itemLabel) ? itemLabel(item.raw) : item.raw[itemLabel];
  const searchInput = (search || '').toString().toLocaleLowerCase();
  const index = rowText.toLocaleLowerCase().indexOf(searchInput);

  if (index < 0) return { start: '', middle: rowText, end: '' };

  const start = rowText.slice(0, index);
  const middle = rowText.slice(index, index + searchInput.length);
  const end = rowText.slice(index + searchInput.length);

  return { start, middle, end };
}

/**
 * Filters a list of items such as workpackages, templates and bundles.
 * The name or description values must match/include the search criteria.
 * @param {String} searchText - Search criteria used to filter items
 * @param {Array<Object>} items - Array of items to be filtered
 * @param {String} sortOrder - Determines how the items are sorted
 * @returns {Array<Object>} - Array of items that match the search criteria
 */
function getResultsByNameDescription(searchText, items, sortOrder) {
  const query = searchText.toLowerCase();

  const resultList = searchText
    ? items.filter(item => {
        const name = get(item, 'name', '').toLowerCase();
        const description = get(item, 'description', '').toLowerCase();

        return name.includes(query) || description.includes(query);
      })
    : items;

  return map(sortUtils.sortItemList(resultList, sortOrder), '_id');
}

/**
 * Searches for a product by any fields in the list, case insensitive.
 * @param {string} searchTerm - The search string to look for.
 * @param {array} fields - The row field list to be checked.
 * @returns {boolean} True if the search string is found in either the fields, false otherwise.
 */
function searchRowsByFields(searchTerm, fields) {
  if (!searchTerm) return true;

  const multiSelectSeparators = {
    // if you match on a character, also match on it + whitespace
    // e.g. a comma should match a single comma and a comma either preceded or succeeded by arbitrary amount of whitespace
    commaAndSpaces: /\s*,\s*/,
    // Row and column copy-paste from Excel
    // Row-copy only gives us single spaces. Column gives a tab character, which is also matched by the whitespace regex.
    // Both ultimately only give spaces in searchbox so we split *only* when digits surround them
    spaces: /\s+/,
  };

  // Trimming leading and terminal whitespace simplifies the handling.
  searchTerm = trim(searchTerm);

  // If commas are present, don't split string - this should be matched exactly
  const termSurroundedByQuotes = /^["'].*["']$/.test(searchTerm);

  if (!termSurroundedByQuotes) {
    // Try multiple separators in order. Only one can be applied - stop once a split is successful
    some(values(multiSelectSeparators), matchPattern => {
      const multiSplit = split(searchTerm, matchPattern);
      if (size(multiSplit) > 1) {
        searchTerm = split(searchTerm, matchPattern);
        return true;
      }
    });
  } else {
    // Quotes are present - remove them
    searchTerm = searchTerm.replaceAll(/["']/g, '');
  }

  // We always compare against an array, ensure the term is one
  if (!isArray(searchTerm)) searchTerm = [searchTerm];
  // Single commas like `123, ` can produce empty string search terms which match all products. Remove these.
  searchTerm = filter(searchTerm);

  const findings = map(fields, field =>
    some(searchTerm, part => new RegExp(part, 'i').test(field || ''))
  );

  return !!find(findings, Boolean);
}

export default {
  getMaskedText,
  getResultsByNameDescription,
  searchRowsByFields,
};
